import {
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/utils/data';
import { isDetachedKey } from 'src/containers/ProjectDiagram/utils/detachedKey';
import {
  getModelConceptClassValue,
  getOrCreateModelConceptClassKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getModelConceptRelationshipKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import { getModelConceptRelationshipLabelKey } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { restoreDeletedRelationship } from 'src/containers/ProjectDiagram/utils/restoreDeletedData';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';

export const changeModelConceptRelationshipConnection =
  ({
    key,
    fromIri,
    toIri,
  }: {
    key: string;
    fromIri?: string;
    toIri?: string;
  }) =>
  (model: Model) => {
    const relationship = model.linkDataArray.find(
      (linkData): linkData is ModelConceptRelationship =>
        isModelConceptRelationship(linkData) && linkData.key === key
    );
    if (!relationship) {
      return { linkData: undefined, linkLabelData: undefined };
    }

    const oldFromIri = relationship.from
      ? getModelConceptClassValue(relationship.from)
      : undefined;
    const oldToIri = relationship.to
      ? getModelConceptClassValue(relationship.to)
      : undefined;

    if (oldFromIri === fromIri && oldToIri === toIri) {
      return { linkData: undefined, linkLabelData: undefined };
    }

    const oldFromKey = getOrCreateModelConceptClassKey(oldFromIri);
    const oldToKey = getOrCreateModelConceptClassKey(oldToIri);
    const fromKey = getOrCreateModelConceptClassKey(fromIri);
    const toKey = getOrCreateModelConceptClassKey(toIri);

    const linkLabelKey = relationship.labelKeys[0];
    const relationshipLabel = model.nodeDataArray.find(
      (nodeData): nodeData is ModelConceptRelationshipLabel =>
        isModelConceptRelationshipLabel(nodeData) &&
        nodeData.key === linkLabelKey
    );

    if (!relationshipLabel) {
      console.warn(new Error(`couldn't find linkLabel ${linkLabelKey}`));
      return { linkData: undefined, relationshipLabel: undefined };
    }

    if (oldFromKey !== fromKey) {
      const newFromKey = isDetachedKey(fromKey) ? undefined : fromKey;
      setDataProperty<ModelConceptRelationship>(model)(
        relationship,
        'from',
        newFromKey
      );
      setDataProperty<ModelConceptRelationshipLabel>(model)(
        relationshipLabel,
        'from',
        newFromKey
      );
    }

    if (oldToKey !== toKey) {
      const newToKey = isDetachedKey(toKey) ? undefined : toKey;
      setDataProperty<ModelConceptRelationship>(model)(
        relationship,
        'to',
        newToKey
      );
      setDataProperty<ModelConceptRelationshipLabel>(model)(
        relationshipLabel,
        'to',
        newToKey
      );
    }

    const { value } = relationship;
    const newFromIri = getModelConceptClassValue(fromKey);
    const newToIri = getModelConceptClassValue(toKey);

    const newLinkKey = getModelConceptRelationshipKey({
      value,
      fromIri: newFromIri,
      toIri: newToIri,
    });

    const newLinkLabelKey = getModelConceptRelationshipLabelKey({
      value,
      fromIri: newFromIri,
      toIri: newToIri,
    });

    setDataProperty<ModelConceptRelationship>(model)(
      relationship,
      'labelKeys',
      [newLinkLabelKey]
    );

    model.setKeyForLinkData(relationship, newLinkKey);
    model.setKeyForNodeData(relationshipLabel, newLinkLabelKey);

    restoreDeletedRelationship(relationship, relationshipLabel)(model);

    return { linkData: relationship, linkLabelData: relationshipLabel };
  };
